/** Rule template library: browse the built-in baselines, inspect and manage saved ones. */

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AppConfig, TemplateSummary } from '../lib/types';
import { Badge, Callout, Spinner } from './primitives';

interface TemplatesViewProps {
  config: AppConfig;
  templates: TemplateSummary[];
  onChanged: () => void;
}

export function TemplatesView({ config, templates, onChanged }: TemplatesViewProps) {
  const [selected, setSelected] = useState<string | null>(templates[0]?.id ?? null);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    api
      .getTemplate(selected)
      .then((body) => {
        if (!cancelled) setDetail(body);
      })
      .catch((caught) => {
        if (!cancelled) {
          setDetail(null);
          setError(caught instanceof ApiError ? caught.message : 'Could not load that template.');
        }
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const active = templates.find((template) => template.id === selected);

  return (
    <div className="grid grid-2-1">
      <div className="stack">
        {active && (
          <div className="card">
            <div className="card-head">
              <h2>{active.name}</h2>
              <div className="masthead-spacer" />
              {active.builtin ? <Badge>Built in</Badge> : <Badge>Saved</Badge>}
              <Badge>{active.sector}</Badge>
            </div>
            <div className="card-body stack">
              <p className="hint">{active.description}</p>
              {error && <Callout variant="error">{error}</Callout>}
              {busy && (
                <div className="row">
                  <Spinner /> <span className="hint-strong">Loading definition…</span>
                </div>
              )}
              {detail && <TemplateDefinition definition={detail} />}
            </div>
          </div>
        )}

        {detail && (
          <div className="card">
            <div className="card-head">
              <h3>Definition (JSON)</h3>
              <div className="masthead-spacer" />
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  api.saveBlob(
                    new Blob([JSON.stringify(detail, null, 2)], { type: 'application/json' }),
                    `${selected}.json`,
                  )
                }
              >
                Download JSON
              </button>
            </div>
            <div className="card-body">
              <pre className="xml-preview">{JSON.stringify(detail, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>

      <div className="stack">
        <div className="card">
          <div className="card-head">
            <h3>Templates</h3>
          </div>
          <div className="card-body">
            <div className="option-list option-list-compact">
              {templates.map((template) => {
                const isSelected = selected === template.id;
                return (
                  <button
                    key={template.id}
                    type="button"
                    className={`option${isSelected ? ' selected' : ''}`}
                    style={{ textAlign: 'left' }}
                    onClick={() => setSelected(template.id)}
                  >
                    <div className="option-body">
                      <div className="option-name">{template.name}</div>
                      {isSelected && <div className="option-desc">{template.description}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {!config.features.saved_templates ? (
          <div className="card">
            <div className="card-body">
              <Callout variant="upsell">
                <strong>Saved templates are a Professional feature.</strong>
                <div style={{ marginTop: '0.2rem' }}>
                  Tailor a baseline to a specific client and re-use it on every submission.
                </div>
              </Callout>
            </div>
          </div>
        ) : (
          <SaveTemplateCard templates={templates} onSaved={onChanged} />
        )}
      </div>
    </div>
  );
}

function TemplateDefinition({ definition }: { definition: Record<string, unknown> }) {
  const checks = (definition.checks ?? {}) as Record<string, Record<string, unknown>>;
  const rows: Array<[string, string]> = [];

  const schema = checks.schema;
  if (schema) {
    rows.push([
      'Schema',
      `${(schema.allowed_schemas as string[] | undefined)?.join(', ') ?? 'any'}${
        schema.require_mvd ? ' · MVD required' : ''
      }`,
    ]);
  }
  const spatial = checks.spatial;
  if (spatial) {
    const required = ['site', 'building', 'storey', 'space']
      .filter((level) => spatial[`require_${level}`])
      .join(', ');
    rows.push(['Spatial hierarchy', required ? `requires ${required}` : 'no levels required']);
  }
  const geometry = checks.geometry;
  if (geometry) {
    rows.push([
      'Geometry',
      `${geometry.severity as string} severity${geometry.check_zero_volume ? ' · zero-volume checked' : ''}`,
    ]);
  }
  const classification = checks.classification;
  if (classification) {
    const systems = (classification.systems as string[] | undefined) ?? [];
    rows.push([
      'Classification',
      `${systems.length ? systems.join(', ') : 'any system'} · ${classification.severity as string}`,
    ]);
  }

  const property = checks.property as
    | { requirements?: Array<Record<string, unknown>>; attributes?: Array<Record<string, unknown>> }
    | undefined;

  return (
    <div className="stack" style={{ gap: '0.9rem' }}>
      <div className="meta-list">
        {rows.map(([key, value]) => (
          <div className="meta-row" key={key}>
            <span className="meta-key">{key}</span>
            <span className="meta-value">{value}</span>
          </div>
        ))}
      </div>

      {property?.requirements && property.requirements.length > 0 && (
        <div>
          <div className="field-label">Property requirements</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Applies to</th>
                  <th>Property set</th>
                  <th>Properties</th>
                  <th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {property.requirements.map((requirement, index) => (
                  <tr key={index}>
                    <td className="cell-mono">
                      {(requirement.applies_to as string[]).join(', ')}
                    </td>
                    <td className="cell-mono">{requirement.pset as string}</td>
                    <td className="cell-mono">
                      {(requirement.properties as string[]).join(', ') || 'set must exist'}
                    </td>
                    <td>{requirement.severity as string}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {property?.attributes && property.attributes.length > 0 && (
        <div>
          <div className="field-label">Required attributes</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Applies to</th>
                  <th>Attribute</th>
                  <th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {property.attributes.map((attribute, index) => (
                  <tr key={index}>
                    <td className="cell-mono">{(attribute.applies_to as string[]).join(', ')}</td>
                    <td className="cell-mono">{attribute.attribute as string}</td>
                    <td>{attribute.severity as string}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SaveTemplateCard({
  templates,
  onSaved,
}: {
  templates: TemplateSummary[];
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [basedOn, setBasedOn] = useState('iso19650-baseline');
  const [definition, setDefinition] = useState('{\n  "checks": {}\n}');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const parsed = JSON.parse(definition) as Record<string, unknown>;
      const created = await api.saveTemplate({
        name: name.trim() || 'Untitled rule set',
        based_on: basedOn || null,
        definition: parsed,
      });
      setSaved(created.name);
      setName('');
      onSaved();
    } catch (caught) {
      if (caught instanceof SyntaxError) setError(`The JSON is not valid: ${caught.message}`);
      else setError(caught instanceof ApiError ? caught.message : 'Could not save that template.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Save a tailored rule set</h3>
      </div>
      <div className="card-body stack">
        <p className="hint">
          Start from a baseline and override only what differs. Anything you omit is inherited.
        </p>
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            value={name}
            placeholder="e.g. Client X — Stage 4"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Based on</span>
          <select value={basedOn} onChange={(event) => setBasedOn(event.target.value)}>
            {templates
              .filter((template) => template.builtin)
              .map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
          </select>
        </label>
        <label className="field">
          <span>Overrides (JSON)</span>
          <textarea
            value={definition}
            onChange={(event) => setDefinition(event.target.value)}
            spellCheck={false}
          />
        </label>
        {error && <Callout variant="error">{error}</Callout>}
        {saved && <Callout>Saved “{saved}”. It is now selectable when you validate.</Callout>}
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save rule set'}
        </button>
      </div>
    </div>
  );
}
